import { AxiosHeaders } from 'axios';
import type { CamelCasedPropertiesDeep, SnakeCasedPropertiesDeep } from 'type-fest';

import { Codeowners } from '../consts/codeowners';
import { ServicePayload } from '../hooks/useTimeSlots';
import { ContactDetailsValues } from '../state/types';
import { ArrivalWindow, AvailableServicesResponse, PricingFormFieldType, ServiceType, TaxInfo } from '../types/types';
import camelcaseKeys from '../utils/camelCaseKeys';
import { catchNetworkExceptions, errorResponseMatcher, mapNetworkStatuses } from '../utils/exception';
import { responseDataToCamelCase } from '../utils/responseDataToCamelCase';

import { countryShortcut } from './countryShortcut';
import { OnlineBookingErrors } from './errors';
import { formatCAZipCode } from './formatCAZipCode';
import RestAPI from './restApi';

export type PaymentConfig = 'full_payment' | 'no_payment' | 'deposit';

export type DepositType = 'percentage' | 'fixed';

export type DepositSettings = {
  type: DepositType;
  amount: number;
};

export type FullPaymentSettings = {
  config: 'full_payment';
};

export type NoPaymentSettings = {
  config: 'no_payment';
};

export type DepositPaymentSettings = {
  config: 'deposit';
  deposit_settings: DepositSettings;
};

export type PaymentSettingsResponse = FullPaymentSettings | NoPaymentSettings | DepositPaymentSettings;

export type PaymentSettings = CamelCasedPropertiesDeep<PaymentSettingsResponse>;

export type OrganizationInfo = CamelCasedPropertiesDeep<{
  logo_url: string;
  time_zone: string;
  default_arrival_window: number;
  email: string;
  online_booking_enabled?: boolean;
  phone_number?: string;
  booking_preferences?: {
    book_as_unscheduled: boolean;
  };
  taxInfo: TaxInfo;
  zip_codes?: string[];
  zip_codes_enabled?: boolean;
  name?: string;
  require_card_on_file?: boolean;
  redirect_url?: string;
  payments_required: boolean;
  payment_settings: PaymentSettingsResponse;
}>;

export type TimeSlot = {
  from: string;
  to: string;
  type: 'exact' | 'range';
};

export type TimeSlotDict = {
  [k: string]: TimeSlot[];
};

export type Availability = {
  date: string;
  time_slots: TimeSlot[];
};

export enum LeadSource {
  DEFAULT = 'DEFAULT',
  RESERVE_WITH_GOOGLE = 'RESERVE_WITH_GOOGLE',
}

export type PricingFormsPayloadForConfirmation = SnakeCasedPropertiesDeep<{
  uuid: string;
  name: string;
  fields: PricingFormFieldType[];
}>;

export type BaseConfirmBookingPayload = {
  service?: ServiceType;
  type?: 'unscheduled';
  description: string;
  contactDetails: {
    submitting: boolean;
    valid: boolean;
    values: ContactDetailsValues;
  };
  arrivalWindow: ArrivalWindow;
  question1: string | null;
  question2: string | null;
  token: string;
  services?: ServicePayload[];
  priceForms?: PricingFormsPayloadForConfirmation[];
  attachments?: string[];
  attr?: string;
  leadSource: LeadSource;
  responseToken: string | null;
  ref?: string;
  source?: 'hcpa' | 'customer_portal';
};

export type ConfirmBookingPayloadWithCustomerPortalData = Omit<BaseConfirmBookingPayload, 'source'> & {
  customerUuid: string;
  source: 'customer_portal';
  customerPortalToken: string;
};

export type ConfirmBookingPaymentDataResponse = {
  booking_uuid: string;
  invoice_token: string;
  payment_required: boolean;
};

export const PAYMENT_INITIATED_EVENT = 'Charge card payment initiated';
export const PAYMENT_CONFIRMED_EVENT = 'Transaction Submitted';
export const PAYMENT_FAILED_EVENT = 'Charge card payment failed';

export type ServerSideEventName =
  | typeof PAYMENT_INITIATED_EVENT
  | typeof PAYMENT_CONFIRMED_EVENT
  | typeof PAYMENT_FAILED_EVENT;

export type SendServerSideEventPayload = {
  domain: 'Online Booking';
  event_name: ServerSideEventName;
  email: string;
  booking_uuid?: string;
  last_4_card_number: string;
  outstanding_balance: number; // always zero since we don't have deposits
  payment_amount: number;
  payment_date: string;
  payment_description?: string;
};

export type SendServerSideEventParams = CamelCasedPropertiesDeep<SendServerSideEventPayload> & { token: string };

export type ConfirmBookingPaymentData = CamelCasedPropertiesDeep<ConfirmBookingPaymentDataResponse>;

export type ConfirmBookingPayload = BaseConfirmBookingPayload | ConfirmBookingPayloadWithCustomerPortalData;
export type ConfirmBookingResponse = ConfirmBookingPaymentDataResponse;

export type UploadDataDetails = {
  fields: {
    key: string;
    'Content-Type': string;
    policy: string;
    'x-amz-credential': string;
    'x-amz-algorithm': string;
    'x-amz-date': string;
    'x-amz-security-token': string;
    'x-amz-signature': string;
  };
  headers: object;
  method: 'post';
  url: string;
};

const CONFIRM_ENDPOINT = `/online_booking/v3/confirm_booking`;
const EXISTING_CUSTOMER_CONFIRM_ENDPOINT = `/online_booking/customer_portal/confirm_booking`;

const prepareHeaders = (token?: string) => {
  const headers = new AxiosHeaders();
  headers.set('Authorization', `Token token=${token}`);
  return headers;
};

export type UploadAttachmentPayload = {
  content_type: string;
};

function isCustomerPortalContext(rest: ConfirmBookingPayload): rest is ConfirmBookingPayloadWithCustomerPortalData {
  return 'source' in rest && rest.source === 'customer_portal';
}

export const tokenRequests = {
  fetchOrgData: (token: string) => {
    return catchNetworkExceptions(
      () =>
        RestAPI.getWithHeaders<OrganizationInfo>(`/online_booking/v2/organization`, prepareHeaders(token)).then(
          (response) => ({
            ...response,
            ...(response?.data ? { data: camelcaseKeys(response.data) } : {}),
          })
        ),
      Codeowners.CUSTOMER_INTAKE,
      errorResponseMatcher({
        [OnlineBookingErrors.orgNotFound]: {
          status: 404,
          path: 'error.message',
          value: OnlineBookingErrors.orgNotFound,
        },
      })
    );
  },
  fetchServices: (token?: string) => {
    return catchNetworkExceptions(
      () =>
        RestAPI.getWithHeaders<AvailableServicesResponse>(
          '/online_booking/v2/available_services',
          prepareHeaders(token)
        ).then((response) => ({
          ...response,
          ...(response?.data ? { data: camelcaseKeys(response.data) } : {}),
        })),
      Codeowners.CUSTOMER_INTAKE,
      mapNetworkStatuses
    );
  },
  fetchTimeSlotsV3: (
    dateFrom: string,
    dateTo: string,
    services?: Array<{ uuid: string; quantity: number }>,
    pricingForms?: Array<{ uuid: string }>,
    token?: string
  ) => {
    return catchNetworkExceptions(
      () =>
        RestAPI.getWithQueryString<
          Availability[],
          {
            date_from: string;
            date_to: string;
            services?: Array<{ uuid: string; quantity: number }>;
            price_forms?: Array<{ uuid: string }>;
          }
        >(
          `/online_booking/v3/available_time_slots`,
          {
            date_from: dateFrom,
            date_to: dateTo,
            ...(services && { services }),
            ...(pricingForms && { price_forms: pricingForms }),
          },
          prepareHeaders(token)
        ),
      Codeowners.CUSTOMER_INTAKE,
      mapNetworkStatuses
    );
  },
  confirmBooking: (params: ConfirmBookingPayload) => {
    const {
      description,
      type,
      token,
      contactDetails: { values },
      arrivalWindow: { date, time },
      question1,
      question2,
      services = [],
      priceForms = [],
      attachments,
      leadSource,
      responseToken,
      attr,
      ref,
    } = params;

    const isCanada = values.country === 'Canada';

    const urlParams = new URLSearchParams(window.location.search);
    const isHCPA = urlParams.get('hcpa') === 'true';

    const url = isCustomerPortalContext(params) ? EXISTING_CUSTOMER_CONFIRM_ENDPOINT : CONFIRM_ENDPOINT;

    const existingCustomerParams = isCustomerPortalContext(params)
      ? {
          source: params.source,
          customer_uuid: params.customerUuid,
          token: params.customerPortalToken,
        }
      : {};

    const optionalParams: Partial<ConfirmBookingPayload> = {
      ...existingCustomerParams,
    };

    if (isHCPA && !isCustomerPortalContext(params)) {
      optionalParams.source = 'hcpa';
    }

    return catchNetworkExceptions(
      () =>
        RestAPI.postWithCustomHeaders<ConfirmBookingResponse, object>(url, prepareHeaders(token), {
          private_notes: `${description}${question1}${question2}`,
          lead_source: leadSource,
          response_token: responseToken,
          type,
          contact_details: {
            first_name: values.firstName,
            last_name: values.lastName,
            phone_number: values.phone,
            email: values.email,
            state: values.state,
            city: values.city,
            zip_code: isCanada ? formatCAZipCode(values.zipCode) : values.zipCode,
            street_address: values.addressLine1,
            unit: values.addressLine2,
            country: countryShortcut(values.country),
          },
          marketing_data_collection_consent: values.consent,
          arrival_window: {
            date,
            start_time: time?.from,
          },
          attr,
          attachments,
          services,
          ref,
          ...(priceForms?.length ? { price_forms: priceForms } : {}),
          ...optionalParams,
        }).then(responseDataToCamelCase),
      Codeowners.CUSTOMER_INTAKE,
      errorResponseMatcher({
        serviceNotFound: {
          status: 422,
          path: 'error',
          value: 'ServiceNotFound',
        },
        slotUnavailable: {
          status: 422,
          path: 'error',
          value: 'SlotUnavailable',
        },
        onlineBookingUnavailable: {
          status: 422,
          path: 'error',
          value: 'OnlineBookingUnavailable',
        },
      })
    );
  },
  getUploadData: ({ mimeType, token }: { token: string; mimeType: string }) => {
    return catchNetworkExceptions(
      () =>
        RestAPI.postWithCustomHeaders<UploadDataDetails, UploadAttachmentPayload>(
          '/online_booking/attachments',
          prepareHeaders(token),
          {
            content_type: mimeType,
          }
        ),
      Codeowners.CUSTOMER_INTAKE,
      mapNetworkStatuses
    );
  },
  sendServerSideEvent: (params: SendServerSideEventParams) => {
    return catchNetworkExceptions(
      () =>
        RestAPI.postWithCustomHeaders<void, SendServerSideEventPayload>(
          '/online_booking/track_events',
          prepareHeaders(params?.token),
          {
            domain: 'Online Booking',
            event_name: params?.eventName,
            email: params.email,
            last_4_card_number: params.last4CardNumber,
            booking_uuid: params.bookingUuid,
            outstanding_balance: params.outstandingBalance,
            payment_amount: params.paymentAmount,
            payment_date: params.paymentDate,
            payment_description: params.paymentDescription,
          }
        ).then(responseDataToCamelCase),
      Codeowners.CUSTOMER_INTAKE,
      mapNetworkStatuses
    );
  },
};
